/**
 * useAndroidOpdatering — in-app opdateringscheck til Capacitor Android-appen
 *
 * Virker KUN inde i Capacitor-WebView (window.Capacitor truthy).
 * I normal browser er hooken passiv (returnerer null-state).
 *
 * Strategi (web-first):
 *   - Henter /app/version.json fra serveren
 *   - Sammenligner manifestets versionCode med APP_VERSION_CODE (kompileret ind)
 *   - Returnerer opdateringsstatus som state
 *   - Åbn apkUrl via window.open() → Android Download-manager henter APK
 *     og tilbyder installation (Android Install Intent)
 *
 * Fordel: fremtidige UI-ændringer i opdateringsbanneret kræver IKKE ny APK —
 * kun web-deploy. Ny APK kræves kun når APP_VERSION_CODE bumpes.
 *
 * APP_VERSION_CODE: matcher versionCode i build.gradle (1.3.0 → 10300)
 * Formel: major*10000 + minor*100 + patch
 */

import { useState, useEffect, useCallback } from 'react';

// Skal matche build.gradle versionCode og workflow-versionering:
// 1.3.0 → 1*10000 + 3*100 + 0 = 10300
export const APP_VERSION_CODE = 10300;
export const APP_VERSION_NAVN = '1.3.0';

const MANIFEST_URL = 'https://app.skilsmissevejen.dk/app/version.json';

// Tjek kun ved app-start og ved forgrunds-hændelse — max én gang per 4 timer
const MIN_CHECK_INTERVAL_MS = 4 * 60 * 60 * 1000;
const STORAGE_KEY = 'sv_apk_last_check';

function erCapacitor() {
  return typeof window !== 'undefined' && !!window.Capacitor;
}

export function useAndroidOpdatering() {
  const [opdatering, setOpdatering] = useState(null);
  // { version: string, versionCode: number, apkUrl: string, noter: string }

  const [installerer, setInstallerer] = useState(false);
  const [afvist, setAfvist] = useState(false);

  const tjekForOpdatering = useCallback(async (tvungen = false) => {
    if (!erCapacitor()) return;
    if (afvist) return;

    // Throttle: undgå gentagne tjek
    if (!tvungen) {
      const sidst = parseInt(sessionStorage.getItem(STORAGE_KEY) || '0', 10);
      if (Date.now() - sidst < MIN_CHECK_INTERVAL_MS) return;
    }

    try {
      // cache-bust så vi altid får frisk manifest
      const res = await fetch(MANIFEST_URL + '?t=' + Date.now(), {
        cache: 'no-store',
        headers: { 'Accept': 'application/json' },
      });
      if (!res.ok) return;

      const manifest = await res.json();
      sessionStorage.setItem(STORAGE_KEY, String(Date.now()));

      const manifestCode = parseInt(manifest.versionCode, 10);
      if (!manifestCode || isNaN(manifestCode)) return;

      if (manifestCode > APP_VERSION_CODE) {
        setOpdatering({
          version: manifest.version || '?',
          versionCode: manifestCode,
          apkUrl: manifest.apkUrl || '',
          noter: manifest.noter || '',
          udgivet: manifest.udgivet || '',
        });
      }
    } catch {
      // Stil-fejl — netværk nede, server ikke klar osv.
    }
  }, [afvist]);

  // Tjek ved mount
  useEffect(() => {
    tjekForOpdatering();
  }, [tjekForOpdatering]);

  // Tjek når appen kommer i forgrunden (Capacitor App-events via document)
  useEffect(() => {
    if (!erCapacitor()) return;

    const onResume = () => tjekForOpdatering(false);
    document.addEventListener('resume', onResume);
    return () => document.removeEventListener('resume', onResume);
  }, [tjekForOpdatering]);

  const installerOpdatering = useCallback(() => {
    if (!opdatering?.apkUrl) return;
    setInstallerer(true);

    // window.open med _system åbner i Android's native browser/download-manager,
    // der henter APK'en og præsenterer installations-dialoggen.
    // _blank fungerer typisk også i Capacitor WebView.
    try {
      window.open(opdatering.apkUrl, '_system');
    } catch {
      // Fallback: direkte location assignment
      window.location.href = opdatering.apkUrl;
    }

    // Reset installerer-flag efter 10 sek (brugeren kan have afvist install-dialog)
    setTimeout(() => setInstallerer(false), 10000);
  }, [opdatering]);

  const afvisOpdatering = useCallback(() => {
    setAfvist(true);
    setOpdatering(null);
    // Gem afvisning i sessionStorage — spørg igen ved næste app-start
    sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
  }, []);

  return {
    opdatering,          // null = ingen opdatering, objekt = ny version tilgængelig
    installerer,         // true mens installation startes
    installerOpdatering,
    afvisOpdatering,
    erCapacitorApp: erCapacitor(),
  };
}
